import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { ApiError, TunnelView, createTunnel, listDevices, updateTunnel } from './api';

const SUBDOMAIN_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Create/edit a persistent tunnel (D14: a proper dialog with inline field
 * errors — never window.prompt). Ephemeral tunnels never reach here; they die
 * with the CLI, so the table offers Kill only.
 */
export function TunnelDialog({
  open,
  tunnel,
  onClose,
}: {
  open: boolean;
  /** null → create mode. */
  tunnel: TunnelView | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const editing = tunnel !== null;

  const [subdomain, setSubdomain] = useState('');
  const [device, setDevice] = useState('');
  const [localHost, setLocalHost] = useState('127.0.0.1');
  const [localPort, setLocalPort] = useState('8080');
  const [rewriteHost, setRewriteHost] = useState(false);
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');

  const devices = useQuery({ queryKey: ['devices'], queryFn: listDevices, enabled: open });

  useEffect(() => {
    if (!open) return;
    setSubdomain(tunnel?.subdomain ?? '');
    setDevice(tunnel?.deviceId ?? '');
    setLocalHost(tunnel?.localHost ?? '127.0.0.1');
    setLocalPort(String(tunnel?.localPort ?? 8080));
    setRewriteHost(tunnel?.rewriteHost ?? false);
    setAuthUsername(tunnel?.authUsername ?? '');
    setAuthPassword('');
  }, [open, tunnel]);

  const save = useMutation({
    mutationFn: async () => {
      const port = Number(localPort);
      if (editing) {
        return updateTunnel(tunnel.id, {
          localHost,
          localPort: port,
          rewriteHost,
          authUsername: authUsername || undefined,
          // Only send the password when it was typed: '' clears basic auth,
          // undefined leaves the existing credential (and generation) alone.
          ...(authPassword !== '' ? { authPassword } : {}),
        });
      }
      return createTunnel({
        subdomain,
        device,
        localHost,
        localPort: port,
        rewriteHost,
        authUsername: authUsername || undefined,
        authPassword: authPassword || undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tunnels'] });
      onClose();
    },
  });

  const portNumber = Number(localPort);
  const portError =
    localPort !== '' && (!Number.isInteger(portNumber) || portNumber < 1 || portNumber > 65535)
      ? 'Port must be between 1 and 65535'
      : '';
  const subdomainError =
    !editing && subdomain !== '' && !SUBDOMAIN_RE.test(subdomain)
      ? 'Lowercase letters, digits and hyphens only (a-z0-9-)'
      : '';
  const conflict = save.error instanceof ApiError && save.error.status === 409;
  const canSave =
    !save.isPending &&
    !portError &&
    !subdomainError &&
    localPort !== '' &&
    (editing || (subdomain !== '' && device !== ''));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Edit ${tunnel.subdomain}` : 'New tunnel'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {save.isError && !conflict && (
            <Alert severity="error">
              {save.error instanceof Error ? save.error.message : 'could not save the tunnel'}
            </Alert>
          )}
          {!editing && (
            <>
              <TextField
                label="Subdomain"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                error={!!subdomainError || conflict}
                helperText={
                  conflict
                    ? `${subdomain} is taken — try ${subdomain}2`
                    : subdomainError || `Public URL: https://${subdomain || '<name>'}.t.dikonek.com`
                }
                autoFocus
                fullWidth
              />
              <TextField
                select
                label="Device"
                value={device}
                onChange={(e) => setDevice(e.target.value)}
                helperText="The enrolled box that runs the app"
                fullWidth
              >
                {(devices.data ?? [])
                  .filter((d) => d.status !== 'revoked')
                  .map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name} {d.online ? '' : '(offline)'}
                    </MenuItem>
                  ))}
              </TextField>
            </>
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Local host"
              value={localHost}
              onChange={(e) => setLocalHost(e.target.value)}
              helperText="Loopback unless the agent allowlists more"
              fullWidth
            />
            <TextField
              label="Local port"
              value={localPort}
              onChange={(e) => setLocalPort(e.target.value.replace(/[^0-9]/g, ''))}
              error={!!portError}
              helperText={portError}
              sx={{ width: 160 }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Switch checked={rewriteHost} onChange={(e) => setRewriteHost(e.target.checked)} />
            }
            label="Rewrite Host header (turn on for Vite/Django-style host checks)"
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Auth username"
              value={authUsername}
              onChange={(e) => setAuthUsername(e.target.value)}
              helperText="Leave blank for a public URL"
              fullWidth
            />
            <TextField
              label="Auth password"
              type="password"
              value={authPassword}
              onChange={(e) => setAuthPassword(e.target.value)}
              helperText={editing ? 'Blank keeps the current one' : 'Sets basic auth'}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : editing ? 'Save' : 'Create tunnel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
